import { CheckCircle, Copy, Edit, ExternalLink, Plus } from 'lucide-react'
import Image from 'next/image'
import { FaFacebook } from 'react-icons/fa'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  facebookAdsAdManagement,
  type FacebookAdsAdManagementResult,
} from 'sprites-types/workflow/tools/facebookAdsAdManagement'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function FacebookAdsAdManagementDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(facebookAdsAdManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as FacebookAdsAdManagementResult

  const isCreate = 'created' in result && result.created

  const name = 'name' in result ? result.name : undefined
  const status = 'status' in result ? result.status : undefined
  const adAccountId = 'adAccountId' in result ? result.adAccountId : undefined

  // Extract metadata from inputs
  const inputs = nodeExecution.inputs.functionArgs as any
  const params = inputs?.action?.params || {}
  const adSetId = params.adSetId
  const creative = params.creative

  const isCarousel = creative?.type === 'carousel'
  const isLink = creative?.type === 'link'
  const isVideo = creative?.type === 'video'
  const isInstantForm = creative?.type === 'instantForm'
  const isCatalog = creative?.type === 'catalog'
  const isCollection = creative?.type === 'collection'

  // Get primary image URL for display
  const getPrimaryImageUrl = () => {
    if (isLink || isInstantForm) {
      return creative?.imageUrl || creative?.images?.square || creative?.images?.landscape || null
    }
    if (isCollection && creative?.coverMedia?.type === 'image') {
      return creative?.coverMedia?.url || null
    }
    return null
  }

  const primaryImageUrl = getPrimaryImageUrl()

  // Strip 'act_' prefix if present for URL
  const accountIdForUrl = adAccountId?.replace(/^act_/, '')

  // Even though the search filter is called search by adgroup id, for some reason this is how
  // we filter for ads by id
  const filterSet = adSetId ? `SEARCH_BY_ADGROUP_ID-STRING\x1EEQUAL\x1E"${result.id}"` : null

  const adsManagerUrl = accountIdForUrl
    ? `https://business.facebook.com/adsmanager/manage/ads?act=${accountIdForUrl}&selected_ad_ids=${result.id}${adSetId ? `&selected_adset_ids=${adSetId}` : ''}${filterSet ? `&filter_set=${encodeURIComponent(filterSet)}` : ''}`
    : null

  const contextActions: ContextAction[] = [
    ...(adsManagerUrl
      ? [
          {
            id: 'open-ads-manager',
            label: 'Open in Ads Manager',
            icon: <ExternalLink className="h-4 w-4" />,
            onClick: () => window.open(adsManagerUrl, '_blank', 'noopener,noreferrer'),
          },
        ]
      : []),
    {
      id: 'copy-id',
      label: 'Copy Ad ID',
      icon: <Copy className="h-4 w-4" />,
      onClick: () => navigator.clipboard.writeText(result.id),
    },
    ...(adsManagerUrl
      ? [
          {
            id: 'copy-url',
            label: 'Copy Ads Manager URL',
            icon: <Copy className="h-4 w-4" />,
            onClick: () => navigator.clipboard.writeText(adsManagerUrl),
          },
        ]
      : []),
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-start gap-4 px-4 py-3">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-blue-100 p-2">
              {isCreate ? (
                <Plus className="h-5 w-5 text-blue-600" />
              ) : (
                <Edit className="h-5 w-5 text-blue-600" />
              )}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <FaFacebook className="h-4 w-4 text-[#0064e0]" />
              <div className="text-sm font-semibold text-gray-900">
                {isCreate ? 'Ad Created' : 'Ad Updated'}
                {isCarousel && <span className="ml-1 text-xs text-gray-600">(Carousel)</span>}
                {isVideo && <span className="ml-1 text-xs text-gray-600">(Video)</span>}
                {isInstantForm && (
                  <span className="ml-1 text-xs text-gray-600">(Instant Form)</span>
                )}
                {isCatalog && <span className="ml-1 text-xs text-gray-600">(Catalog)</span>}
                {isCollection && <span className="ml-1 text-xs text-gray-600">(Collection)</span>}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
              {name && <div className="mb-1 text-sm font-medium text-gray-900">{name}</div>}
              <div className="space-y-1 text-xs">
                <div className="text-gray-500">Ad ID: {result.id}</div>
                {adSetId && <div className="text-gray-500">Ad Set ID: {adSetId}</div>}
                {adAccountId && <div className="text-gray-500">Ad Account: {adAccountId}</div>}

                {/* Image Preview */}
                {primaryImageUrl && (
                  <div className="mb-2 mt-3">
                    <Image
                      src={primaryImageUrl}
                      alt="Ad preview"
                      width={800}
                      height={200}
                      className="w-full rounded-lg border border-gray-200 object-cover"
                      style={{ maxHeight: '200px' }}
                      unoptimized
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  </div>
                )}

                {/* Video Preview */}
                {isVideo && creative?.videoUrl && (
                  <div className="mb-2 mt-3 text-gray-700">
                    <video
                      src={creative.videoUrl}
                      controls
                      className="w-full rounded-lg border border-gray-200"
                      style={{ maxHeight: '200px' }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  </div>
                )}

                {creative && (
                  <>
                    {/* Ad Copy Preview */}
                    {creative.message && (
                      <div className="mt-2 rounded-md border border-gray-200 bg-white p-2">
                        <div className="mb-0.5 text-xs text-gray-500">Primary Text:</div>
                        <div className="text-sm text-gray-900">{creative.message}</div>
                      </div>
                    )}
                    {creative.headline && (
                      <div className="mt-1.5 rounded-md border border-gray-200 bg-white p-2">
                        <div className="mb-0.5 text-xs text-gray-500">Headline:</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {Array.isArray(creative.headline)
                            ? creative.headline.filter(Boolean).join(' • ')
                            : String(creative.headline)}
                        </div>
                      </div>
                    )}
                    {creative.description && (
                      <div className="mt-1.5 rounded-md border border-gray-200 bg-white p-2">
                        <div className="mb-0.5 text-xs text-gray-500">Description:</div>
                        <div className="text-xs text-gray-700">
                          {Array.isArray(creative.description)
                            ? creative.description.filter(Boolean).join(' • ')
                            : String(creative.description)}
                        </div>
                      </div>
                    )}
                    {isLink && creative.link && (
                      <div className="text-gray-700">
                        Destination:{' '}
                        <a
                          href={creative.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {creative.link.length > 40
                            ? creative.link.substring(0, 40) + '...'
                            : creative.link}
                        </a>
                      </div>
                    )}
                    {isLink && creative.callToActionType && (
                      <div className="text-gray-700">
                        CTA:{' '}
                        <span className="font-medium">
                          {creative.callToActionType.replace(/_/g, ' ')}
                        </span>
                      </div>
                    )}
                    {isCarousel && creative.cards && (
                      <div className="mt-2 space-y-1.5">
                        <div className="font-medium text-gray-700">
                          Carousel Cards ({creative.cards.length}):
                        </div>
                        {creative.cards.map((card: any, index: number) => {
                          const cardImageUrl =
                            card.imageUrl || card.images?.square || card.images?.landscape
                          return (
                            <div
                              key={index}
                              className="rounded border border-gray-200 bg-white p-2"
                            >
                              <div className="mb-1 font-medium text-gray-600">Card {index + 1}</div>
                              {cardImageUrl && (
                                <Image
                                  src={cardImageUrl}
                                  alt={`Card ${index + 1}`}
                                  width={400}
                                  height={120}
                                  className="mb-2 w-full rounded border border-gray-100 object-cover"
                                  style={{ maxHeight: '120px' }}
                                  unoptimized
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none'
                                  }}
                                />
                              )}
                              {card.headline && (
                                <div className="mb-1 text-gray-700">
                                  <span className="font-medium">
                                    {Array.isArray(card.headline)
                                      ? card.headline.filter(Boolean).join(' • ')
                                      : String(card.headline)}
                                  </span>
                                </div>
                              )}
                              {card.description && (
                                <div className="mb-1 text-xs text-gray-600">
                                  {Array.isArray(card.description)
                                    ? card.description.filter(Boolean).join(' • ')
                                    : String(card.description)}
                                </div>
                              )}
                              {card.link && (
                                <div className="text-xs text-gray-600">
                                  <a
                                    href={card.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    {card.link.length > 35
                                      ? card.link.substring(0, 35) + '...'
                                      : card.link}
                                  </a>
                                </div>
                              )}
                              {card.callToActionType && (
                                <div className="mt-1 text-xs text-gray-600">
                                  CTA: {card.callToActionType.replace(/_/g, ' ')}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                    {isVideo && (
                      <>
                        {creative.callToActionLink && (
                          <div className="text-gray-700">
                            Destination:{' '}
                            <a
                              href={creative.callToActionLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-blue-600 hover:underline"
                            >
                              {creative.callToActionLink.length > 40
                                ? creative.callToActionLink.substring(0, 40) + '...'
                                : creative.callToActionLink}
                            </a>
                          </div>
                        )}
                        {creative.callToActionType && (
                          <div className="text-gray-700">
                            CTA:{' '}
                            <span className="font-medium">
                              {creative.callToActionType.replace(/_/g, ' ')}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    {isInstantForm && (
                      <>
                        {creative.leadgenFormId && (
                          <div className="mt-2 text-gray-700">
                            <span className="text-xs text-gray-500">Form ID:</span>{' '}
                            <span className="font-mono text-xs font-medium">
                              {creative.leadgenFormId}
                            </span>
                          </div>
                        )}
                        {creative.callToActionType && (
                          <div className="text-gray-700">
                            CTA:{' '}
                            <span className="font-medium">
                              {creative.callToActionType.replace(/_/g, ' ')}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
              {status && (
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      status === 'ACTIVE'
                        ? 'bg-green-100 text-green-800'
                        : status === 'PAUSED'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {status}
                  </span>
                </div>
              )}
              {adsManagerUrl && (
                <div className="mt-2">
                  <a
                    href={adsManagerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="h-3 w-3" />
                    <span>View in Ads Manager</span>
                  </a>
                </div>
              )}
            </div>

            {isCreate && (
              <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                <CheckCircle className="h-3 w-3 text-green-600" />
                <span>Ad is ready to use</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function downloadFacebookAdsAdManagement(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function facebookAdsAdManagementToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(facebookAdsAdManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as FacebookAdsAdManagementResult
  const isCreate = 'created' in result && result.created

  const name = 'name' in result ? result.name : undefined
  const status = 'status' in result ? result.status : undefined
  const adAccountId = 'adAccountId' in result ? result.adAccountId : undefined

  // Extract metadata from inputs
  const inputs = nodeExecution.inputs.functionArgs as any
  const params = inputs?.action?.params || {}
  const adSetId = params.adSetId
  const creative = params.creative

  const isCarousel = creative?.type === 'carousel'
  const isLink = creative?.type === 'link'
  const isVideo = creative?.type === 'video'
  const isInstantForm = creative?.type === 'instantForm'

  let adType = ''
  if (isCarousel) adType = ' (Carousel)'
  if (isVideo) adType = ' (Video)'
  if (isInstantForm) adType = ' (Instant Form)'

  let markdown = `## Facebook Ad ${isCreate ? 'Created' : 'Updated'}${adType}\n\n`
  if (name) markdown += `**Name:** ${name}\n`
  markdown += `**Ad ID:** ${result.id}\n`
  if (adSetId) markdown += `**Ad Set ID:** ${adSetId}\n`
  if (adAccountId) markdown += `**Ad Account:** ${adAccountId}\n`
  if (creative) {
    if (creative.message) markdown += `**Message:** ${creative.message}\n`
    if (isLink && creative.link) markdown += `**Destination:** ${creative.link}\n`
    if (isLink && creative.callToActionType)
      markdown += `**CTA:** ${creative.callToActionType.replace(/_/g, ' ')}\n`
    if (isCarousel && creative.cards) {
      markdown += `\n**Carousel Cards (${creative.cards.length}):**\n\n`
      creative.cards.forEach((card: any, index: number) => {
        markdown += `${index + 1}. `
        if (card.name) markdown += `**${card.name}**\n   `
        if (card.link) markdown += `Link: ${card.link}\n   `
        if (card.callToActionType)
          markdown += `CTA: ${card.callToActionType.replace(/_/g, ' ')}\n   `
        markdown += '\n'
      })
    }
    if (isVideo) {
      if (creative.title) markdown += `**Title:** ${creative.title}\n`
      if (creative.description)
        markdown += `**Description:** ${Array.isArray(creative.description) ? creative.description.filter(Boolean).join(' • ') : String(creative.description)}\n`
      if (creative.videoUrl) markdown += `**Video URL:** ${creative.videoUrl}\n`
      if (creative.callToActionLink) markdown += `**Destination:** ${creative.callToActionLink}\n`
      if (creative.callToActionType)
        markdown += `**CTA:** ${creative.callToActionType.replace(/_/g, ' ')}\n`
    }
    if (isInstantForm) {
      if (creative.leadgenFormId) markdown += `**Form ID:** ${creative.leadgenFormId}\n`
      if (creative.imageUrl) markdown += `**Image URL:** ${creative.imageUrl}\n`
      if (creative.callToActionType)
        markdown += `**CTA:** ${creative.callToActionType.replace(/_/g, ' ')}\n`
    }
  }
  if (status) markdown += `**Status:** ${status}\n`
  markdown += '\n---\n'

  return markdown
}

export const FacebookAdsAdManagementDisplay = createWorkflowToolComponent(
  FacebookAdsAdManagementDisplayComponent,
  downloadFacebookAdsAdManagement,
  facebookAdsAdManagementToMarkdown
)
